import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check for redirect query parameter (e.g. ?redirect=/courses/backend-dev?mode=offline) or router state
  const searchParams = new URLSearchParams(location.search);
  const redirectQuery = searchParams.get('redirect');
  const redirectPath = redirectQuery || location.state?.from || '/courses';

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const cleanDigits = value.replace(/\D/g, '').slice(0, 10);
      setFormData({ ...formData, phone: cleanDigits });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Form validations
    if (!formData.name.trim()) {
      setError('Please provide your full name.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    const phoneDigits = formData.phone.trim().replace(/\D/g, '');
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(phoneDigits)) {
      setError('Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match. Please verify.');
      return;
    }

    try {
      setLoading(true);
      await register({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: phoneDigits,
        password: formData.password
      });

      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Student Account | Durbhasi Gurukulam Courses</title>
        <meta name="description" content="Register as a student at Durbhasi Gurukulam to enroll in live systems engineering cohorts and classroom batches." />
      </Helmet>

      <div className="auth-page-wrapper">
        <div className="auth-card-container">
          {/* Left Brand Panel */}
          <div className="auth-brand-panel">
            <div>
              <div className="auth-brand-header">
                <img
                  src="/durbhasi-gurukulam-logo.png"
                  alt="Durbhasi Gurukulam"
                  className="auth-brand-logo"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div>
                  <h1 className="auth-brand-title">Durbhasi <span>Gurukulam</span></h1>
                  <span className="auth-brand-tagline">Systems &amp; Software Engineering</span>
                </div>
              </div>

              <div className="auth-hero-copy">
                <h2>Join the Elite Engineering Cohort</h2>
                <p>
                  Create your student profile to access mentor-guided code reviews, production architecture defense, and verified credentials.
                </p>

                <ul className="auth-perks-list">
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Direct Cohort Enrollment &amp; Payment Hub</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Jaipur Classroom Campus Access (Radha Vihar)</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Official Tax Receipts &amp; Verified Credential ID</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Private Discord &amp; Mentor Q&amp;A Channels</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="auth-brand-footer">
              🛡️ Zero Spam • Confidential Student Profiles
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="auth-form-panel">
            <span className="auth-form-kicker">Free Student Registration</span>
            <h2 className="auth-form-title">Create Your Student Account</h2>
            <p className="auth-form-subtext">
              Already have an account?{' '}
              <Link to={redirectQuery ? `/login?redirect=${encodeURIComponent(redirectQuery)}` : '/login'} state={location.state}>
                Sign in here &rarr;
              </Link>
            </p>

            {redirectQuery && (
              <div
                style={{
                  background: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#1d4ed8',
                  fontSize: '0.86rem'
                }}
              >
                <span>ℹ️</span>
                <span><strong>Student Registration Required:</strong> Create your free account to enroll in this cohort.</span>
              </div>
            )}

            {error && (
              <div className="auth-error-alert">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-input-group">
                <label className="auth-input-label">
                  Full Name <span>*</span>
                </label>
                <div className="auth-input-box">
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="e.g. Rahul Sharma"
                    className="auth-input-field"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="name"
                  />
                </div>
              </div>

              <div className="auth-input-group">
                <label className="auth-input-label">
                  Email Address <span>*</span>
                </label>
                <div className="auth-input-box">
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="rahul@example.com"
                    className="auth-input-field"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="auth-input-group">
                <label className="auth-input-label">
                  WhatsApp / Phone Number <span>*</span>
                </label>
                <div className="auth-input-box">
                  <input
                    type="tel"
                    name="phone"
                    required
                    inputMode="numeric"
                    pattern="[6-9][0-9]{9}"
                    maxLength={10}
                    placeholder="e.g. 9876543210 (10 digits)"
                    className="auth-input-field"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="tel"
                  />
                </div>
                <span className="auth-helper-text">10-digit mobile number for cohort orientation and classroom pass.</span>
              </div>

              <div className="auth-input-group">
                <label className="auth-input-label">
                  Create Password <span>*</span>
                </label>
                <div className="auth-input-box">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    required
                    placeholder="Minimum 6 characters"
                    className="auth-input-field"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="auth-pw-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className="auth-input-group">
                <label className="auth-input-label">
                  Confirm Password <span>*</span>
                </label>
                <div className="auth-input-box">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    required
                    placeholder="Re-type your password"
                    className="auth-input-field"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Complete Registration &rarr;'}
              </button>
            </form>

            <div className="auth-card-footer">
              <span>By creating an account, you agree to Durbhasi Gurukulam's </span>
              <Link to="/support">Terms &amp; Student Code of Conduct</Link>.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Register;
